import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { supabase } from "~/integrations/supabase/client";

import type {
  ClientGoalRow,
  SalesGoalRow,
  SellerMonthlyGoals,
} from "../types/salesGoals";

export const useSalesGoals = (
  year: number,
  sellerProfileId?: string | null
) => {
  // Query 1: Clients with goals
  const goalsQuery = useQuery<SalesGoalRow[]>({
    queryKey: ["sales-goals", year],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_sales_goals_vs_actual", {
        p_year: year,
      } as any);
      if (error) throw error;
      return (data as SalesGoalRow[]) || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  // Query 2: Clients without goals (sales only)
  const noGoalsQuery = useQuery<SalesGoalRow[]>({
    queryKey: ["sales-without-goals", year],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_sales_without_goals", {
        p_year: year,
      } as any);
      if (error) throw error;
      return (data as SalesGoalRow[]) || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  // Merge both datasets
  const filteredData = useMemo(() => {
    const goalsData = goalsQuery.data || [];
    const noGoalsData = noGoalsQuery.data || [];
    const combined = [...goalsData, ...noGoalsData];

    if (!sellerProfileId) return combined;
    return combined.filter((r) => r.seller_id === sellerProfileId);
  }, [goalsQuery.data, noGoalsQuery.data, sellerProfileId]);

  const currentMonth =
    year === dayjs().year()
      ? dayjs().month() + 1
      : year < dayjs().year()
        ? 12
        : 0;

  const bySeller = useMemo<SellerMonthlyGoals[]>(() => {
    if (!filteredData.length) return [];
    const map = new Map<
      string,
      {
        name: string;
        months: Record<number, number>;
        actuals: Record<number, number>;
        total: number;
        totalActual: number;
      }
    >();

    for (const r of filteredData) {
      const key = r.seller_id || "unassigned";
      if (!map.has(key)) {
        map.set(key, {
          name: r.seller_name,
          months: {},
          actuals: {},
          total: 0,
          totalActual: 0,
        });
      }
      const s = map.get(key)!;
      s.months[r.month] = (s.months[r.month] || 0) + r.goal_amount;
      s.actuals[r.month] = (s.actuals[r.month] || 0) + r.actual_amount;
      s.total += r.goal_amount;
      s.totalActual += r.actual_amount;
    }

    return Array.from(map.entries())
      .map(([id, s]) => ({
        seller_id: id === "unassigned" ? null : id,
        seller_name: s.name,
        months: s.months,
        actuals: s.actuals,
        total: s.total,
        totalActual: s.totalActual,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredData]);

  const clients = useMemo<ClientGoalRow[]>(() => {
    if (!filteredData.length) return [];
    const map = new Map<string, ClientGoalRow>();

    for (const r of filteredData) {
      if (!map.has(r.client_id)) {
        map.set(r.client_id, {
          client_id: r.client_id,
          client_name: r.client_name,
          client_tax_id: r.client_tax_id,
          seller_name: r.seller_name,
          sales_channel_name: r.sales_channel_name,
          months: {},
          actuals: {},
          total: 0,
          totalActual: 0,
        });
      }
      const c = map.get(r.client_id)!;
      c.months[r.month] = (c.months[r.month] || 0) + r.goal_amount;
      c.actuals[r.month] = (c.actuals[r.month] || 0) + r.actual_amount;
      c.total += r.goal_amount;
      c.totalActual += r.actual_amount;
    }

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filteredData]);

  return {
    isLoading: goalsQuery.isLoading || noGoalsQuery.isLoading,
    bySeller,
    clients,
    currentMonth,
  };
};
